import {Injectable} from '@angular/core';
import {ReportFilters, TimeFilterValue} from 'app/features/analytics/service/filter-value.interface';
import {isEqualDeep} from 'app/core/helpers/object';
import {RichStatsVariables} from 'app/features/analytics/service/gql/RichStats';
import {isTimeDimension} from 'app/features/analytics/shared/utils';
import {excludeDateDimensionsAndMapFilters, isAnonymizeMode, mapGroupBy} from 'app/features/analytics/service/stats.helpers';
import {DateDimension, HavingFilterInput, HavingFilterOperator} from '../../../../globalTypes';
import {TraceService} from 'app/features/analytics/service/trace.service';

const compareFnById = (a, b) => {
    if (a.id === b.id) {
        return 0;
    }
    return a.id > b.id ? -1 : 1;
};

@Injectable()
export class StatsHelperService {
    constructor(public trace: TraceService) {}

    compareFilters = (a: ReportFilters, b: ReportFilters): boolean =>
        a.isDemo === b.isDemo && isEqualDeep(this.richStatsRequestParams(a), this.richStatsRequestParams(b));

    richStatsRequestParams(reportFilters: ReportFilters): RichStatsVariables {
        if (!reportFilters) {
            return null;
        }

        const anyDateFilter = reportFilters.filter.find(filter => isTimeDimension(filter.id)) as TimeFilterValue;
        if (!(anyDateFilter && anyDateFilter.from && anyDateFilter.to)) {
            return null;
        }

        const filterParams = {
            isMultiMediation: reportFilters.isMultiMediation || false,
            forecastVersion: reportFilters.forecastVersion || '',
            topFilter: reportFilters.topFilter || null,
            filters: excludeDateDimensionsAndMapFilters(reportFilters.filter).sort(compareFnById),
            funnelFilter: reportFilters.funnelFilter,
            dateFilters: reportFilters.filter
                .filter(f => isTimeDimension(f.id))
                .map((dateFilter: TimeFilterValue) => ({
                    dimension: dateFilter.id as unknown as DateDimension,
                    from: dateFilter.from,
                    to: dateFilter.to,
                    include: true,
                }))
                .sort(compareFnById),
            groupBy: reportFilters.groupBy.length ? reportFilters.groupBy.map(group => mapGroupBy(group)) : [],
            measures: reportFilters.measures.map(metric => ({id: metric.id, day: metric.day === undefined ? null : metric.day})),
            having: reportFilters.measures
                .filter(metric => metric.having && Object.keys(metric.having))
                .map(metric =>
                    Object.keys(metric.having).map(
                        (operator): HavingFilterInput => ({
                            measure: {id: metric.id, day: metric.day === undefined ? null : metric.day},
                            operator: operator as HavingFilterOperator,
                            value: Number(metric.having[operator]),
                        })
                    )
                )
                .flat(),
            anonymizationMode: isAnonymizeMode(reportFilters.mode),
        };

        return {
            dateFilters: filterParams.dateFilters,
            filters: filterParams.filters,
            funnelFilter: filterParams.funnelFilter,
            groupBy: filterParams.groupBy,
            measures: filterParams.measures,
            havingFilters: filterParams.having,
            anonymizationMode: filterParams.anonymizationMode,
            topFilter: filterParams.topFilter,
            revenuePredictionVersion: filterParams.forecastVersion,
            isMultiMediation: filterParams.isMultiMediation,
        };
    }
}
